// Layout-Bausteine für kleine Schirme (Issue #479).
//
// Anlagenschema und Signalgraph sind Canvas-Ansichten mit zwei Seitenpanelen (Palette links,
// Eigenschaften rechts). Am Schreibtisch ist das ein festes Drei-Spalten-Raster. Auf dem Handy
// bliebe von der Canvas nichts übrig — 16rem + 20rem Panele sind breiter als der ganze Viewport.
// Deshalb fahren die Panele unterhalb der md-Grenze als Schublade über die Canvas und liegen
// darüber wieder fest in ihrer Spalte.

import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { useI18n } from '../i18n';

// Ein Seitenpanel, das ab `md` fest in der Rasterspalte steht und darunter als Overlay-Schublade
// erscheint. `open` steuert nur den Handy-Fall; auf breiten Schirmen ist das Panel immer sichtbar.
//
// Bewusst über CSS-Klassen statt über einen matchMedia-Hook: das Panel bleibt im DOM, die Canvas
// wird beim Umschalten nicht neu gemountet und React-Flow verliert seinen Ausschnitt nicht.
export function SidePanel({
  side,
  open,
  onClose,
  title,
  testId,
  children,
}: {
  side: 'left' | 'right';
  open: boolean;
  onClose: () => void;
  title: string;
  testId?: string;
  children: ReactNode;
}): JSX.Element {
  const { t } = useI18n();

  // Escape schließt die Schublade — auf dem Handy mit Tastatur und im Test der schnellste Weg raus.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const edge = side === 'left' ? 'left-0 border-r' : 'right-0 border-l';
  const closed = side === 'left' ? '-translate-x-full' : 'translate-x-full';

  return (
    <>
      {/* Backdrop nur auf dem Handy: Tippen daneben schließt. */}
      {open && (
        <button
          type="button"
          aria-label={t('common.close')}
          data-testid={testId ? `${testId}-backdrop` : undefined}
          onClick={onClose}
          className="fixed inset-0 z-20 bg-bf-navy/80 md:hidden"
        />
      )}
      <aside
        data-testid={testId}
        data-open={open ? 'true' : 'false'}
        className={
          'fixed inset-y-0 z-30 flex w-[86vw] max-w-sm flex-col gap-3 overflow-y-auto ' +
          'border-bf-border bg-bf-navy p-3 transition-transform duration-200 ' +
          'md:static md:z-auto md:w-auto md:max-w-none md:translate-x-0 md:bg-transparent ' +
          `${edge} ${open ? 'translate-x-0' : closed}`
        }
      >
        {/* Panel-Überschrift; der Schließen-Knopf gehört nur zur Handy-Schublade. */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-bf-muted">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            data-testid={testId ? `${testId}-close` : undefined}
            className="rounded border border-bf-border px-2 py-1 text-sm text-bf-white hover:border-bf-gold md:hidden"
          >
            {t('common.close')}
          </button>
        </div>
        {children}
      </aside>
    </>
  );
}

// Werkzeugleiste über der Canvas, die es nur auf dem Handy gibt: sie holt die beiden Panele
// hervor, die dort als Schublade weggeklappt sind.
export function CanvasDrawerBar({
  leftLabel,
  rightLabel,
  onOpenLeft,
  onOpenRight,
  testIdPrefix,
}: {
  leftLabel: string;
  rightLabel: string;
  onOpenLeft: () => void;
  onOpenRight: () => void;
  testIdPrefix: string;
}): JSX.Element {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-bf-border p-2 md:hidden">
      <button
        type="button"
        onClick={onOpenLeft}
        data-testid={`${testIdPrefix}-open-palette`}
        className="rounded border border-bf-border px-3 py-1.5 text-sm text-bf-white hover:border-bf-gold"
      >
        {leftLabel}
      </button>
      <button
        type="button"
        onClick={onOpenRight}
        data-testid={`${testIdPrefix}-open-properties`}
        className="rounded border border-bf-border px-3 py-1.5 text-sm text-bf-white hover:border-bf-gold"
      >
        {rightLabel}
      </button>
    </div>
  );
}

// Ist der Viewport schmal (unterhalb der md-Grenze, also Handy)? Für die wenigen Entscheidungen,
// die sich nicht als CSS-Klasse ausdrücken lassen — etwa der Zoom, mit dem React-Flow einen Graphen
// beim Öffnen einrahmt.
export function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = (): void => setNarrow(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  return narrow;
}
